import React, { useCallback, useMemo, useState } from "react";
import cls from "classnames";
import { Space } from "@al/akui-mobile";
import { useT, useCtx } from "@al/next/al-ex";
import dayjs from "dayjs";
import { PriceTools, CountryCode } from "@al/base-lib";

import { CouponItemModel, DiscountType, CouponStatus, TimeLimitType } from "~/common/model/coupon";

import { usePriceFormat } from "../../../hooks/usePriceFormat";
import Iconfont from "../../Iconfont";

// import CouponStatusMark from "./CouponStatusMark";
import styles from "./styles.scss";

export interface CouponItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * 操作类型：
   * get 领取
   * use 使用
   * select 选择
   */
  operateType: "get" | "use" | "select";
  /**
   * 是否禁用
   */
  disabled?: boolean;
  data: CouponItemModel;
  checked?: boolean;
  /**
   * 关闭详情
   */
  noDescription?: boolean;
  /**
   * 显示不可用原因，使用时，不能使用 noDescription 属性
   */
  showAvailableReason?: boolean;
  couponStatus?: CouponStatus;
  /**
   * 是否已领取
   */
  hasReceived?: boolean;
  /**
   * 用于 operateType 为 select 时
   */
  onSelect?: () => void;
  /**
   * 用于 operateType 为 use/get 时
   */
  onClickOperateBtn?: () => void;
}

export const CouponItem = ({
  data,
  disabled,
  className,
  noDescription,
  showAvailableReason,
  couponStatus,
  // hasReceived,
  onSelect,
  ...rest
}: CouponItemProps): JSX.Element => {
  const t = useT();
  const ctx = useCtx();
  const countryCode = ctx.locale.countryCode;
  const priceFormat = usePriceFormat();
  const [showDesc, setShowDesc] = useState(false);

  const [intValue, floatValue] = useMemo(() => {
    return (
      {
        [DiscountType.FullReduction]: () => [
          PriceTools.formatPrice(countryCode as CountryCode, data.faceAmount, "price"),
          "",
        ],
        [DiscountType.FullDiscount]: () => [
          PriceTools.formatPrice(countryCode as CountryCode, data.faceAmount, "price", false),
          "% OFF",
        ],
      }[data.discountType]?.() || ""
    );
  }, [data]);

  const handleShowDesc = useCallback(() => {
    !noDescription && setShowDesc(!showDesc);
  }, [showDesc]);

  const disableUse = useMemo(() => {
    return (couponStatus && [CouponStatus.Used, CouponStatus.Expired].includes(couponStatus)) || disabled;
  }, [couponStatus, disabled]);

  return (
    <div
      className={cls(styles.coupon, className, {
        [styles.disabled]: disableUse,
      })}
      {...rest}
    >
      <Space block direction="vertical" justify="between" className={styles.coupon_main}>
        <div className={styles.coupon_header}>
          <Iconfont className={styles.coupon_type_icon} name={"shopping"} />
          <span className={styles.coupon_name}>{data.name}</span>
        </div>
        <div className={styles.coupon_content}>
          <div>
            <div className={styles.coupon_price}>
              <span className={styles.coupon_price_int}>{intValue}</span>
              {floatValue && <span className={styles.coupon_price_float}>{floatValue}</span>}
            </div>
            <div className={styles.coupon_price_explain}>
              {t("满{{amount}}元可用", {
                amount: `${priceFormat(data.thresholdAmount, "price", true)}`,
              })}
            </div>
            {/* <CouponStatusMark
            couponStatus={couponStatus}
            className={hasReceived && styles.has_received_mark}
            hasReceived={hasReceived}
          /> */}
          </div>
          <div className={styles.coupon_btn} onClick={onSelect}>
            {t("领取")}
          </div>
        </div>
      </Space>
      <div className={styles.coupon_bottom}>
        <Space
          block
          justify="between"
          className={cls(styles.coupon_bottom_title, {
            [styles.coupon_bottom_padding_bottom]: showDesc,
          })}
          onClick={!noDescription ? handleShowDesc : undefined}
        >
          {data?.timeLimitType === TimeLimitType.Fixed
            ? t("领取后{{x}}天可用", { x: data.expireDay })
            : `${dayjs(Number(data.startTime)).format("HH:mm:ss DD/MM/YYYY ")} - ${dayjs(Number(data.endTime)).format(
                "HH:mm:ss DD/MM/YYYY"
              )}`}
          {!noDescription && <Iconfont className={styles.expand_icon} name={showDesc ? "collapse" : "expand"} />}
        </Space>
        <div
          className={cls(styles.coupon_bottom_content, {
            [styles.show]: showDesc,
          })}
        >
          {showAvailableReason
            ? data.unAvailableReason
            : data.description?.split("\n")?.map((item) => <div key={item}>{`${item.slice(1)}.`}</div>)}
        </div>
      </div>
    </div>
  );
};
